import C_GameEvent from "../const/C_GameEvent";
import MathUtil from "../utils/MathUtil";
import Point3D from "../math/Point3D";

/**
 * Used to position and size graphical elements. The basic (default) coordinate system is rect.
 */
class Layout
{


    /**
     * The Layout class creates two positioning rectangles
     * <p> rect (0,0,w,h) - this is default if no other positioning object
     * is passed as a parameter to a positioning/sizing method </p>
     * <p> portraitRect - a rectangle, whose width is the narrower side of the display
     * </pre>
     * @param w - the width of the default positioning rect
     * @param h - the height of the default positioning rect
     */
    constructor(w, h)
    {
        this.app = PIXI.app;
        this.stage = this.app.stage;
        this.startW = w;
        this.startH = h;
        this.rect =  new PIXI.Rectangle(0, 0, w, h);

        this.fov = Math.PI/1.5;
        this.halfFov = this.fov/2;
        this.screenWidth = 20;
        this.zDistance = this.screenWidth * .5 / Math.cos(this.halfFov/2);

        this.activeShakes = {};
        this.shakeProperties = {};
        this.keyCounter = 0;
    }


    setIsMobile(isMobile)
    {
        this.isMobile = isMobile;
    }


    getMobile()
    {
        return this.isMobile;
    }


    /**
     * Is the device in landscape mode ?
     * @public
     * @return {boolean}
     */
    isLandscape()
    {
        return this.landscape;
    }


    /**
     * Execute recalculations needed when screen is resized.
     * @public
     * @param w {Number} width
     * @param h {Number} height
     */
    onResize(w, h)
    {
        this.deviceWidth = w;
        this.deviceHeight = h;

        this.landscape = w > h;

        if (this.landscape)
        {
            this.portraitRect =  new PIXI.Rectangle(0, 0, this.startW, this.startW * this.startW / this.startH);
            this.screenToGameRatio = this.relWidth(100 + this.app.config.viewPadSize)/5;
        }
        else
        {
            this.portraitRect =  new PIXI.Rectangle(0, 0, this.startW, this.startW * h/w);
            this.screenToGameRatio = this.relHeight(100 + this.app.config.viewPadSize)/5;
        }

        if (this.app.dispatcher)
        {
            this.app.dispatcher.emit(C_GameEvent.RESIZE);
        }

        this.xOrigin = this.relWidth(50 + this.app.config.halfViewPadSize);
        this.yOrigin = this.relHeight(50 + this.app.config.halfViewPadSize);


    }


    /**
     * Set position of a target object relative to a source
     * @public
     * @param target - display object
     * @param x - x position (percent of source object)
     * @param y - y position (percent of source object)
     * @param src - either a display object or a rectangle
     */
    setPos(target, x, y, src)
    {
        this.horizontalAlignLeft(target, x, src);
        this.verticalAlignTop(target, y, src);
    }


    /**
     * Set  size and position of a target object relatively to a source
     * @public
     * @param target - display object
     * @param h - height (percent)
     * @param x - x position (percent of source object)
     * @param y - y position (percent of source object)
     * @param src - either a display object or a rectangle
     */
    setHeightAndPos(target, h, x, y, src)
    {
        this.propHeightScale(target, h, src);
        this.setPos(target, x, y, src);
    }


    /**
     * Calculates a percentage of the width of the source
     * @public
     * @param w - width (in percent)
     * @param src - either a display object or a rectangle
     * @returns {number}
     */
    relWidth(w, src)
    {
        if (src == undefined)
        {
            src = this.rect;
        }

        return w * src.width  / 100;
    }


    /**
     * Calculates a percentage of the height of the source
     * @public
     * @param h - width (in percent)
     * @param src - either a display object or a rectangle
     * @returns {number}
     */
    relHeight(h, src)
    {
        if (src == undefined)
        {
            src = this.rect;
        }

        return h * src.height / 100;
    }


    /**
     * Scales a target, preserving its aspect relative to a source
     * @public
     * @param target - display object
     * @param scale - required scale in percent
     * @param src - display object or rectangle
     */
    propWidthScale(target, scale, src)
    {
        if (src == undefined)
        {
            src = this.rect;
        }

        let requiredScale = src.width * scale/100 / target.width * target.scale.x;
        target.scale.x = requiredScale;
        target.scale.y = requiredScale;
    }


    /**
     * Scales a target, preserving its aspect relative to a source
     * @public
     * @param target - display object
     * @param scale - required scale in percent
     * @param src - display object or rectangle
     */
    propHeightScale(target, scale, src)
    {
        if (src == undefined)
        {
            src = this.rect;
        }

        let requiredScale = src.height * scale/100 / target.height * target.scale.y;
        target.scale.x = requiredScale;
        target.scale.y = requiredScale;
    }


    /**
     * Horizontally align left a target relative to a source
     * @public
     * @param target - display object
     * @param distance - distance in percent
     * @param src - display object or rectangle
     */
    horizontalAlignLeft(target, distance=0, src=undefined)
    {
        if (src == undefined)
        {
            src = this.rect;
        }
        target.x = src.x + src.width * distance/100;
    }


    /**
     * Horizontally align right a target relative to a source
     * @public
     * @param target {PIXI.DisplayObject} display object
     * @param distance {Number} distance in percent
     * @param src {PIXI.DisplayObject} display object or rectangle
     */
    horizontalAlignRight(target, distance=0, src=undefined)
    {
        if (src == undefined)
        {
            src = this.rect;
        }
        target.x = src.x + src.width * (100 - distance) / 100 - target.width;
    }


    /**
     * Horizontally align center a target relative to a source
     * @public
     * @param target - display object
     * @param distance - distance in percent
     * @param src - display object or rectangle
     */
    horizontalAlignCenter(target, distance=0, src=undefined)
    {
        if (src == undefined)
        {
            src = this.rect;
        }

        let aX = target.anchor ? target.anchor.x : 0;

        target.x = src.x + src.width * (50 + distance)/100 - target.width * (.5 - aX) ;
    }


    /**
     * Vertically align top a target relative to a source
     * @public
     * @param target - display object
     * @param distance - distance in percent
     * @param src - display object or rectangle
     */
    verticalAlignTop(target, distance=0, src=undefined)
    {
        if (src == undefined)
        {
            src = this.rect;
        }
        target.y = src.y + src.height * (distance)/100;
    }


    /**
     * Vertically align middle a target relative to a source
     * @public
     * @param target - display object
     * @param distance - distance in percent
     * @param src - display object or rectangle
     */
    verticalAlignMiddle(target, distance=0, src=undefined)
    {
        if (src == undefined)
        {
            src = this.rect;
        }
        
        let aY = target.anchor ? target.anchor.y : 0;

        target.y = src.y + src.height * (50 + distance)/100 - target.height * (.5 - aY);
    }


    /**
     * Vertically align bottom a target relative to a source
     * @public
     * @param target - display object
     * @param distance - distance in percent
     * @param src - display object or rectangle
     */
    verticalAlignBottom(target, distance=0, src=undefined)
    {
        if (src == undefined)
        {
            src = this.rect;
        }
        target.y = src.y + src.height * (100-distance)/100 - target.height;
    }


    /**
     * Return the coordinates of a global point as percentage of the layout rectangle
     * @public
     * @param globalPt - global point
     * @return {PIXI.Point}
     */
    getRelCoords(globalPt)
    {
        let relX = globalPt.x * 100 / this.deviceWidth;
        let relY = globalPt.y * 100 / this.deviceHeight;

        // console.log(this.rect);
        // console.log(this.portraitRect);
        let rect = this.landscape ? this.rect : this.portraitRect;

        let ptX = relX/100 * rect.width;
        let ptY = relY/100 * rect.height;

        return new PIXI.Point(ptX, ptY);
    }


    /**
     * Apply layout to children of a container spacing them equally on the vertical
     * @public
     * @param container
     * @param vStep - the distance between the children
     */
    applyVerticalLayout(container, vStep)
    {
        let numChildren = container.children.length;
        let totalHeight = 0;
        let rect = new PIXI.Rectangle(0,0,container.width, container.height);
        for (let childIdx=0; childIdx<numChildren; childIdx++)
        {
            let child = container.getChildAt(childIdx);
            this.horizontalAlignCenter(child, 0, rect);
            child.y = totalHeight;
            totalHeight += (child.height + vStep);
        }
    }


    /**
     * Apply layout to children of a container spacing them equally on the horizontal
     * @public
     * @param container
     * @param hStep - the distance between the children
     */
    applyHorizontalLayout(container, hStep)
    {
        let numChildren = container.children.length;
        let totalWidth = 0;
        let rect = new PIXI.Rectangle(0, 0, container.width, container.height);
        for (let childIdx=0; childIdx < numChildren; childIdx++)
        {
            let child = container.getChildAt(childIdx);
            child.x = totalWidth;
            this.verticalAlignMiddle(child, 0, rect);
            totalWidth += (child.width + hStep);
        }
    }


    /**
     *
     * @param pt3d {Point3D}
     * @param h
     */
    projectGameToScreen(pt3d, h = 0)
    {
        let projected = new Point3D();

        // screenToGameRatio - based on it the sprites are smaller or bigger,
        // but it also changes the whole 3D positioning
        let zRatio = this.zDistance * this.screenToGameRatio / (this.zDistance + pt3d.z);

        projected.x = this.xOrigin + pt3d.x * zRatio;
        projected.y = this.yOrigin + pt3d.y * zRatio;
        projected.z = h * zRatio;

        // console.log("screenToGameRatio: " + this.screenToGameRatio);
        // console.log("gameToScreenRatio X: " + this.getGameToScreenXRatio());
        // console.log("gameToScreenRatio Y: " + this.getGameToScreenYRatio());
        // console.log("zRatio: " + zRatio);
        // console.log("x: " + projected.x);
        // console.log("y: " + projected.y);

        return projected;
    }


    projectScreenToGame(screenPt)
    {
        let projected = new Point3D();

        projected.x = (this.relWidth(screenPt.x - 50)) / this.screenToGameRatio * 1.2;
        projected.y = (this.relHeight(screenPt.y - 51.5)) / this.screenToGameRatio * 1.2;
        projected.z = this.zDistance;

        return projected;
    }


    projectScreenToGame3D(screenPt, z)
    {
        let projected = new Point3D();

        let zRatio = this.zDistance * this.screenToGameRatio / (this.zDistance + z);

        projected.x = (screenPt.x - this.xOrigin)/zRatio;
        projected.y = (screenPt.y - this.yOrigin)/zRatio;
        projected.z = z;

        return projected;
    }


    getGameToScreenXRatio()
    {
        return ((this.relWidth(100 + this.app.config.viewPadSize)*.5)/this.screenToGameRatio);
    }


    getGameToScreenYRatio()
    {
        let aspectRatio = (this.relWidth(100 + this.app.config.viewPadSize))/(this.relHeight(100 + this.app.config.viewPadSize));
        return this.getGameToScreenXRatio()/aspectRatio;
    }


    // returns the X or Y for point in 3d for Screen Coordinates
    getProjectedConst(z)
    {
        return (z + this.app.layout.zDistance)/ this.app.layout.zDistance;
    }


    globalToRelative(globalPt)
    {
        let relPt = new PIXI.Point();
        relPt.x = MathUtil.clamp(globalPt.x / this.deviceWidth, 0, 1);
        relPt.y = MathUtil.clamp(globalPt.y / this.deviceHeight, 0, 1);
        return relPt;
    }


    // Used to make the Target show above the touched area in mobile
    getGlobalPtDiff(pt, tap)
    {
        let ptDif = pt.clone();
        if (this.getMobile() == true && !tap)
        {
            // let maxDif = this.deviceHeight*.25;
            // let difCalc = (this.deviceHeight - pt.y)*.8;
            // ptDif.y -= difCalc > maxDif ? maxDif : difCalc;

            let difCalc = (this.deviceHeight - pt.y)*.6;
            ptDif.y -= difCalc;
        }
        return ptDif;
    }


    shake(displayObject, duration, xAmplitude, yAmplitude, amplitudeModulation, delay)
    {
        if (amplitudeModulation == undefined)
        {
            amplitudeModulation = 1;
        }

        if (delay == undefined)
        {
            delay = 0;
        }

        var key = "key" + this.keyCounter;

        // Check if object already has shake applied , if so the previous one is killed

        let shakeInProgress = false;

        for (let k in this.activeShakes)
        {
            if (this.activeShakes[k] == displayObject)
            {
                // console.log("Overwrite shake : " + k);

                let props = this.shakeProperties[k];
                props.tween.kill();

                var inProgressX = displayObject.x;
                var inProgressY = displayObject.y;

                displayObject.x = props.startX;
                displayObject.y = props.startY;

                delete this.activeShakes[k];
                delete this.shakeProperties[k];

                shakeInProgress = true;
            }
        }


        this.activeShakes[key] = displayObject;

        var tween = TweenMax.to(key, duration,
            {
                onUpdate:this.updateShake,
                onUpdateParams:[key],
                onUpdateScope:this,
                onComplete:this.finishShake,
                onCompleteScope:this,
                onCompleteParams:[key],
                delay:delay
            });

        this.shakeProperties[key] =
            {
                xAmpl:xAmplitude,
                yAmpl:yAmplitude,
                startX:displayObject.x,
                startY:displayObject.y,
                amplMod:amplitudeModulation,
                tween:tween
            };

        if (shakeInProgress == true)
        {
            displayObject.x = inProgressX;
            displayObject.y = inProgressY;
        }

        this.keyCounter++;
    };



    updateShake(index)
    {
        var displayObject = this.activeShakes[index];
        var properties = this.shakeProperties[index];

        if (properties)
        {
            displayObject.x = properties.startX - properties.xAmpl + Math.random()*properties.xAmpl*2;
            displayObject.y = properties.startY - properties.yAmpl + Math.random()*properties.yAmpl*2;

            properties.xAmpl *= properties.amplMod;
            properties.yAmpl *= properties.amplMod;
        }
    }


    finishShake (index)
    {
        var displayObject = this.activeShakes[index];
        var properties = this.shakeProperties[index];

        displayObject.x = properties.startX;
        displayObject.y = properties.startY;

        delete this.activeShakes[index];
        delete this.shakeProperties[index];
    }



}

export default Layout;